import React, { useEffect, useState } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Mascot } from "../brand/Mascot";
import { Scribble, Tape, HandNote } from "../brand/Doodles";
import { useRespectsReducedMotion } from "../../design/motion";

export const Hero = () => {
  const reduced = useRespectsReducedMotion();
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (reduced) return setEntered(true);
    const t = setTimeout(() => setEntered(true), 60);
    return () => clearTimeout(t);
  }, [reduced]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden ggm-grain"
      style={{
        // Tighter mobile top, spacious desktop retained
        paddingTop: "clamp(104px, 12vh, 180px)",
        paddingBottom: "clamp(56px, 10vh, 140px)",
      }}
    >
      {/* Ambient grid + orbital glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(900px 520px at 80% 45%, rgba(11,60,220,0.16), transparent 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-[58%_42%] items-center gap-8 lg:gap-8">
        {/* LEFT: copy */}
        <div
          className="relative"
          style={{
            transform: entered ? "translateY(0)" : "translateY(20px)",
            opacity: entered ? 1 : 0,
            transition: `opacity var(--ggm-duration-hero) var(--ggm-ease-out), transform var(--ggm-duration-hero) var(--ggm-ease-out)`,
          }}
        >
          <div className="mb-4 md:mb-6">
            <HandNote testId="hero-eyebrow" className="text-base md:text-lg">
              AI builder · automation · vibe coding
            </HandNote>
          </div>

          <h1
            className="font-condensed font-bold text-white"
            style={{
              fontSize: "clamp(2.75rem, 7vw, 5.75rem)",
              lineHeight: 0.95,
              letterSpacing: "-0.025em",
            }}
            data-testid="hero-headline"
          >
            You Imagine It.
            <br />
            <span className="relative inline-block" style={{ color: "var(--ggm-signal-cyan)" }}>
              I Build It.
              <Scribble className="absolute left-0 -bottom-3 w-full h-5" />
            </span>
          </h1>

          <p
            className="mt-5 md:mt-7 font-body text-white/75 text-base md:text-lg leading-relaxed max-w-[34ch] md:max-w-xl"
            data-testid="hero-sub"
          >
            AI automations. Internal tools. Web apps.{" "}
            <br className="hidden md:block" />
            If you've got a workflow that's eating time (or an idea you can't ship), I
            turn it into working software in 7–30 days.
          </p>

          {/* CTA group — tighter on mobile so primary/secondary read as one action group */}
          <div className="mt-6 md:mt-9 flex flex-wrap items-center gap-3 md:gap-4">
            <a
              href="#start-a-project"
              className="ggm-btn-primary text-base md:text-lg"
              style={{ padding: "14px 24px" }}
              data-testid="hero-cta-primary"
            >
              Get 3 automation ideas <ArrowUpRight size={18} />
            </a>
            <a
              href="#work"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-sm text-white/80 hover:text-[var(--ggm-signal-cyan)] transition-colors"
              style={{ transitionDuration: "var(--ggm-duration-ui)", minHeight: 44 }}
              data-testid="hero-cta-secondary"
            >
              See what I've built <ArrowDown size={16} />
            </a>
          </div>

          <p
            className="mt-3 font-condensed uppercase tracking-widest text-[11px] text-white/55"
            data-testid="hero-cta-microcopy"
          >
            No sales team. No automated pitch. You'll hear from me.
          </p>
          <p
            className="mt-2 font-body text-white/80 text-sm md:text-base leading-relaxed max-w-xl"
            data-testid="hero-free-audit"
          >
            <span className="font-condensed uppercase tracking-widest text-[11px] text-[var(--ggm-signal-cyan)] font-bold">
              Free 30 minutes
            </span>{" "}
            — your choice: a virtual scoping call,{" "}
            <span className="text-white">or</span> an in-person{" "}
            <span className="text-white font-bold">Local AI Audit at your business</span>{" "}
            (within 15 miles — I'll come to you). One or the other, no obligation.{" "}
            <Link
              to="/audit"
              className="underline text-white hover:text-[var(--ggm-signal-cyan)]"
              data-testid="hero-audit-link"
            >
              What's in the audit?
            </Link>
          </p>

          {/* Credibility strip — desktop */}
          <div
            className="mt-10 md:mt-12 pt-5 md:pt-6 border-t hidden sm:block"
            style={{ borderColor: "var(--ggm-border-subtle)" }}
            data-testid="hero-proof-strip"
          >
            <p className="font-condensed uppercase tracking-widest text-[11px] text-white/70">
              <span className="text-white font-bold">Founder-led AI studio run by Robert.</span>{" "}
              <span style={{ color: "var(--ggm-signal-cyan)" }}>·</span>{" "}
              <span className="text-white font-bold">14 live products.</span>{" "}
              <span style={{ color: "var(--ggm-signal-cyan)" }}>·</span>{" "}
              Built with AI.
            </p>
            <p
              className="mt-3 font-body text-white/55 text-xs leading-relaxed max-w-xl"
              data-testid="hero-bonus"
            >
              <span className="text-white/75">
                Bonus: For builds booked by Dec 31, I'll include 12 months of hosting
                + basic maintenance (a $75/mo value), then $75/mo after year one.
              </span>{" "}
              Domains, third-party tools, and usage-based AI/API costs are separate.
            </p>
          </div>

          {/* Credibility strip — mobile */}
          <div
            className="mt-8 pt-5 border-t sm:hidden"
            style={{ borderColor: "var(--ggm-border-subtle)" }}
            data-testid="hero-proof-strip-mobile"
          >
            <p className="font-condensed uppercase tracking-widest text-[10px] text-white/70 leading-relaxed">
              <span className="text-white font-bold">Founder-led AI studio run by Robert.</span>{" "}
              <span className="text-white font-bold">14 live products.</span>{" "}
              Built with AI.
            </p>
            <p
              className="mt-3 font-body text-white/55 text-[11px] leading-relaxed"
            >
              <span className="text-white/80">
                Bonus: For builds booked by Dec 31, I'll include 12 months of hosting
                + basic maintenance (a $75/mo value), then $75/mo after year one.
              </span>{" "}
              Domains, third-party tools, and usage-based AI/API costs are separate.
            </p>
          </div>
        </div>

        {/* RIGHT: taped polaroid of the shop mascot */}
        <div className="relative hidden lg:block">
          <HeroPolaroid entered={entered} />
        </div>
      </div>

      {/* Mobile mascot — transparent artwork, no mask needed */}
      <div className="relative lg:hidden max-w-sm mx-auto mt-6 px-5" data-testid="hero-mobile-mascot-wrap">
        <Mascot
          pose="heroDefault"
          size="lg"
          loading="eager"
          fetchPriority="high"
          className="mx-auto"
          style={{ height: 280, width: "auto" }}
        />
      </div>
    </section>
  );
};

const HeroPolaroid = ({ entered }) => (
  <div
    className="relative mx-auto"
    style={{
      width: 400,
      opacity: entered ? 1 : 0,
      transform: entered ? "translateY(0)" : "translateY(24px)",
      transition: "opacity 900ms var(--ggm-ease-out) 220ms, transform 1200ms var(--ggm-ease-out) 220ms",
    }}
    data-testid="hero-polaroid"
  >
    <figure className="ggm-polaroid relative" style={{ transform: "rotate(2.5deg)" }}>
      <Tape />
      <div className="flex items-end justify-center overflow-hidden" style={{ background: "var(--ggm-navy)", height: 400 }}>
        <Mascot pose="heroDefault" size="xl" loading="eager" fetchPriority="high" style={{ height: 380, width: "auto" }} />
      </div>
      <figcaption className="ggm-hand pt-3 text-center text-lg" style={{ color: "var(--ggm-navy)" }}>
        Gadget Guy, shop mascot
      </figcaption>
    </figure>
  </div>
);

export default Hero;
